"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Confirmaciones y peticiones de texto DENTRO de la página (en vez de confirm()/prompt() del
// navegador). Un único <DialogoProvider> en el layout; uso: `if (await dialogo.confirmar("…"))`.

type OpcionesConfirmar = { titulo?: string; aceptar?: string; peligro?: boolean };
type OpcionesTexto = { titulo?: string; aceptar?: string; inicial?: string; placeholder?: string };

type Pendiente =
  | ({ tipo: "confirmar"; mensaje: string; resolver: (v: boolean) => void } & OpcionesConfirmar)
  | ({ tipo: "texto"; mensaje: string; resolver: (v: string | null) => void } & OpcionesTexto);

type DialogoApi = {
  confirmar: (mensaje: string, opciones?: OpcionesConfirmar) => Promise<boolean>;
  pedirTexto: (mensaje: string, opciones?: OpcionesTexto) => Promise<string | null>;
};

const DialogoContext = createContext<DialogoApi | null>(null);

export function DialogoProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("dialogo");
  const [actual, setActual] = useState<Pendiente | null>(null);
  const [texto, setTexto] = useState("");
  const resuelto = useRef(false);

  const confirmar = useCallback(
    (mensaje: string, opciones: OpcionesConfirmar = {}) =>
      new Promise<boolean>((resolver) => {
        resuelto.current = false;
        setActual({ tipo: "confirmar", mensaje, resolver, ...opciones });
      }),
    [],
  );
  const pedirTexto = useCallback(
    (mensaje: string, opciones: OpcionesTexto = {}) =>
      new Promise<string | null>((resolver) => {
        resuelto.current = false;
        setTexto(opciones.inicial ?? "");
        setActual({ tipo: "texto", mensaje, resolver, ...opciones });
      }),
    [],
  );
  const api = useMemo(() => ({ confirmar, pedirTexto }), [confirmar, pedirTexto]);

  const cerrar = (aceptado: boolean) => {
    if (!actual || resuelto.current) return;
    resuelto.current = true;
    if (actual.tipo === "confirmar") actual.resolver(aceptado);
    else actual.resolver(aceptado && texto.trim() ? texto.trim() : null);
    setActual(null);
  };

  return (
    <DialogoContext.Provider value={api}>
      {children}
      <Dialog open={!!actual} onOpenChange={(o) => !o && cerrar(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{actual?.titulo ?? (actual?.tipo === "texto" ? actual.mensaje : t("confirmar"))}</DialogTitle>
            {actual?.tipo === "confirmar" && <DialogDescription className="whitespace-pre-line">{actual.mensaje}</DialogDescription>}
          </DialogHeader>
          {actual?.tipo === "texto" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                cerrar(true);
              }}
            >
              <Input autoFocus value={texto} placeholder={actual.placeholder} onChange={(e) => setTexto(e.target.value)} />
            </form>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => cerrar(false)}>
              {t("cancelar")}
            </Button>
            <Button
              variant={actual?.tipo === "confirmar" && actual.peligro ? "destructive" : "default"}
              onClick={() => cerrar(true)}
              disabled={actual?.tipo === "texto" && !texto.trim()}
            >
              {actual?.aceptar ?? t("aceptar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogoContext.Provider>
  );
}

export function useDialogo(): DialogoApi {
  const ctx = useContext(DialogoContext);
  if (!ctx) throw new Error("useDialogo debe usarse dentro de <DialogoProvider>");
  return ctx;
}
